import { type FC, useEffect, useRef, useState } from "react";
import PreviewTable, { type PreviewTableRef } from "~/features/bulkAddToCart/components/PreviewTable/PreviewTable";
import { type PreviewItem, type PreviewItemUpdateData } from "~/features/bulkAddToCart/types/models";
import CheckMessagesComponent from "~/common/components/CheckMessagesComponent";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";
import { Stack, Switch, Typography } from "~/common/mui";
import { t } from "i18next";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

interface Props {
  items: PreviewItem[];
  onDataChange: (index: number, newData: Partial<PreviewItemUpdateData>) => void;
  onRemoveItem: (index: number) => void;
  onRemoveAllItems: () => void;
  onChangeAllCommentsVisibility: (visible: boolean) => void;
  onChangeCommentVisibility: (index: number, visible: boolean) => void;
  saleOnly: boolean;
  setSaleOnly: (visible: boolean) => void;
}

const BulkAtcPopupPreviewStep: FC<Props> = (props) => {
  const {
    items,
    onRemoveItem,
    onDataChange,
    onChangeAllCommentsVisibility,
    onChangeCommentVisibility,
    onRemoveAllItems,
    saleOnly,
    setSaleOnly,
  } = props;

  const [filteredItems, setFilteredItems] = useState(items);
  useEffect(() => {
    setFilteredItems(items.filter((item) => !saleOnly || item.onSale));
  }, [saleOnly, items]);

  const errorMsgCount = filteredItems.reduce(
    (sum, item) => sum + item.messages.filter((msg) => msg.type === "error").length,
    0,
  );
  const infoMsgCount = filteredItems.reduce(
    (sum, item) => sum + item.messages.filter((msg) => msg.type === "info").length,
    0,
  );

  const listRef = useRef<PreviewTableRef>(null);

  const scrollToMsg = (toRowMap: { [msgIndex: number]: number }) => (msgIndex: number) => {
    const list = listRef.current;
    if (list) {
      const rowIndex = toRowMap[msgIndex];
      list.scrollToRow(rowIndex);
    }
  };

  const infoMsgMap = createMessageToRowMap(filteredItems, "info");
  const scrollToInfoMsg = scrollToMsg(infoMsgMap);

  const errorMsgMap = createMessageToRowMap(filteredItems, "error");
  const scrollToErrorMsg = scrollToMsg(errorMsgMap);

  const count = filteredItems.length;
  const totalQty = filteredItems.map((item) => item.quantity).reduce((a, b) => a + b, 0);

  const numberOfOnSaleProducts = filteredItems.filter((item) => item.onSale).length;
  const showOnSaleToggle = numberOfOnSaleProducts > 0;

  return (
    <>
      <SuccessSnackBar count={count} totalQty={totalQty} />

      <div className={"scha__bulk-preview__check-messages-container mb-16"}>
        <Stack gap={1}>
          {errorMsgCount > 0 && (
            <CheckMessagesComponent
              className={"w-100"}
              msgType={"error"}
              msgCount={errorMsgCount}
              onScrollToMessage={scrollToErrorMsg}
              isBulkUploadPreview={true}
            />
          )}

          {errorMsgCount === 0 && infoMsgCount > 0 && (
            <CheckMessagesComponent
              className={"w-100"}
              msgType={"info"}
              msgCount={infoMsgCount}
              onScrollToMessage={scrollToInfoMsg}
              isBulkUploadPreview={true}
            />
          )}
        </Stack>
      </div>

      {showOnSaleToggle && (
        <Stack direction="row" gap={1} alignItems="center" sx={{ p: 2 }}>
          <Switch checked={saleOnly} onChange={() => setSaleOnly(!saleOnly)} />
          <Typography variant="bodyLarge">
            {t("bulkentry.sale.only.toggle", { count: numberOfOnSaleProducts })}
          </Typography>
          <SaleTag size={16} />
        </Stack>
      )}

      <PreviewTable
        listRef={listRef}
        items={filteredItems}
        onDataChange={onDataChange}
        onRemoveItem={onRemoveItem}
        onChangeAllCommentsVisibility={onChangeAllCommentsVisibility}
        onChangeCommentVisibility={onChangeCommentVisibility}
        onRemoveAllItems={onRemoveAllItems}
        saleOnly={saleOnly}
        setSaleOnly={setSaleOnly}
      />
    </>
  );
};
export default BulkAtcPopupPreviewStep;

const createMessageToRowMap = (entries: PreviewItem[], type: "error" | "info") => {
  const map: { [key: number]: number } = {};
  let msgCounter = 0;
  entries.forEach((row, rowIndex) => {
    row.messages.filter((msg) => msg.type === type).forEach(() => (map[msgCounter++] = rowIndex));
  });
  return map;
};

const SuccessSnackBar = ({ count, totalQty }: { count: number; totalQty: number }) => {
  const { val: snackBar, setFalse: closeSnackbar } = useBooleanState(true);
  const { t } = useTranslation();

  return (
    <Snackbar
      open={snackBar}
      onClose={closeSnackbar}
      message={t("bulkentry.success.message", { items: count, pieces: totalQty })}
      action={<SnackbarCloseButton onClick={closeSnackbar} />}
    />
  );
};
